import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { usePersona, identityProvider } from "@/context/PersonaContext";

/**
 * Screen 0 · Sign in.
 * Static SSO mimic. No password field. No validation. No storage.
 * "Continue with Meridian SSO" shows a 1-second redirecting state and
 * sets the active persona to J. Okafor (the demo default).
 */
export default function SignIn() {
  const navigate = useNavigate();
  const { setActiveId } = usePersona();
  const [email, setEmail] = useState("j.okafor@meridiansurgical.com");
  const [redirecting, setRedirecting] = useState(false);

  const onContinue = () => {
    if (redirecting) return;
    setRedirecting(true);
    setTimeout(() => {
      setActiveId("jokafor");
      navigate("/");
    }, 1000);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6"
      style={{ background: "var(--mos-bg)" }}
      data-testid="page-signin"
    >
      <div
        className="w-full max-w-[440px] rounded-lg border shadow-sm p-8 mos-fade"
        style={{
          background: "var(--mos-surface)",
          borderColor: "var(--mos-border)",
        }}
      >
        <div className="flex flex-col items-center text-center">
          <BrandMark size={44} variant="tile" />
          <div className="mt-5 flex items-baseline gap-2">
            <span
              className="text-[22px] font-semibold tracking-tight"
              style={{ color: "var(--mos-accent)" }}
            >
              Manufacturing
              <span className="mos-mono font-medium" style={{ color: "#64748B" }}>
                OS
              </span>
            </span>
          </div>
          <div className="text-[13px] text-slate-500 mt-1">
            Meridian Surgical Systems
          </div>
        </div>

        <div className="mt-8 space-y-4">
          <label className="block">
            <span className="text-[11px] tracking-[0.18em] uppercase font-semibold text-slate-500">
              Work email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={redirecting}
              data-testid="signin-email"
              className="mt-1.5 w-full rounded-md border px-3 py-2.5 text-[14px] mos-mono focus:outline-none focus:ring-2 focus:ring-[color:var(--mos-accent-ring)] focus:border-[color:var(--mos-accent)]"
              style={{
                borderColor: "var(--mos-border-strong)",
                background: redirecting ? "#F8FAFC" : "var(--mos-surface)",
                color: "var(--mos-text)",
              }}
            />
          </label>

          {redirecting ? (
            <div
              className="w-full rounded-md border px-4 py-3 flex items-start gap-3 text-[13.5px]"
              style={{
                background: "#EFF3F9",
                borderColor: "var(--mos-border)",
                color: "var(--mos-accent)",
              }}
              data-testid="signin-redirecting"
              role="status"
              aria-live="polite"
            >
              <Loader2 size={15} className="animate-spin flex-none mt-0.5" />
              <div className="flex-1 min-w-0">
                <div>
                  Redirecting to Meridian Surgical identity provider…
                </div>
                <div
                  className="mt-1 flex items-center gap-1.5 text-[11.5px] text-slate-500"
                  data-testid="signin-idp"
                >
                  <ShieldCheck size={10} />
                  <span>via</span>
                  <span className="mos-mono text-slate-700">
                    {identityProvider}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={onContinue}
              className="mos-btn mos-btn-primary w-full justify-center py-3"
              data-testid="btn-continue-sso"
            >
              Continue with Meridian SSO
              <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>

      <p
        className="mt-6 text-[12px] text-slate-500 max-w-[440px] text-center leading-relaxed"
        data-testid="signin-footer"
      >
        Single sign on via your identity provider. ManufacturingOS holds no
        passwords.
        <br />
        <span
          className="inline-flex items-center gap-1 mt-1.5 mos-mono text-[11px] text-slate-400"
          data-testid="signin-footer-idp"
        >
          <ShieldCheck size={10} />
          Federated via {identityProvider}
        </span>
      </p>
    </div>
  );
}
